import { fetchSsrStyles, waitForElement } from "../../core/common/ssr-utils.js";
import { loadModuleScriptsFromHtml } from "../../core/common/ssr-scripts.js";
import { getSsrBaseUrl } from "./coursesu-env.js";
import { getPosExtId, isAvailable } from "./utils";


const DISPLAY_RECIPE_VARIANT = 2;
const RECIPE_CARD_MULTIPLE_HTML_ENDPOINT = "/v3/recipe-card/multiple";
const RECIPE_CARD_MULTIPLE_STYLES_ENDPOINT =
  `/v3/styles/recipe-card/multiple?recipe_card_variant=${DISPLAY_RECIPE_VARIANT}`;

let productsAndCardPositions = [];
let ssrCardsById = null;
const hiddenPositions = new Set(); // Track positions that should stay hidden
let injectTimeout = null; // Add debouncing for injection
let removeTimeout = null; // Add debouncing for removal

export async function initShelvesCardInjection() {
  // Injects recipes on shelves
  if(await isAvailable()){
    productsAndCardPositions = cardsPositionsByScreenWidth();
    injectCards();
    const pageListenerInject = new MutationObserver(() => {
      // Debounce injection to prevent excessive calls
      clearTimeout(injectTimeout);
      injectTimeout = setTimeout(() => injectCards(), 100);
    });
    pageListenerInject.observe(document.body, { attributes: false, childList: true, subtree: true });
    const pageListenerRemove = new MutationObserver(() => {
      // Debounce removal to prevent excessive calls
      clearTimeout(removeTimeout);
      removeTimeout = setTimeout(() => removeCardsIfNecessary(), 100);
    });
    pageListenerRemove.observe(document.body, { attributes: false, childList: true, subtree: true });
  }
}

/* ******************************************************* SSR PREFETCH ******************************************************** */

/**
 * Prefetch recipe-cards from the ids of the preceding and following products
 * Does not inject the cards into the DOM, but stores them in the `ssrCardsById` object
 * This allows for the scripts to be loaded before the cards are injected into the DOM, avoiding FOUC
 * or problems of window.mealz being undefined for the main.js script execution.
 */
export async function prefetchRecipeCards(headers) {
  if (!(await isAvailable())) {
    return;
  }

  const container = await waitForElement(".tiles-container", {
    maxAttempts: 20,
    intervalMs: 250,
  });
  if (!container) {
    console.warn("[Mealz] .tiles-container not found for recipe cards");
    return;
  }

  try {
    fetchSsrStyles(RECIPE_CARD_MULTIPLE_STYLES_ENDPOINT, getSsrBaseUrl());

    const contexts = buildRecipeCardContexts(container.children);
    if (contexts.length === 0) {
      return;
    }

    const data = await fetchRecipeCardMultiple(headers, contexts);
    await Promise.all(data.map(({ html }) => loadModuleScriptsFromHtml(html)));

    ssrCardsById = Object.fromEntries(
      data.map(({ position, html }) => [Number(position), html])
    );
  } catch (err) {
    console.warn("[Mealz] recipe cards SSR prefetch failed", err);
  }
}

function fetchRecipeCardMultiple(headers, contexts) {
  const baseUrl = getSsrBaseUrl();
  if (!baseUrl) {
    throw new Error("[Mealz] baseUrl not configured");
  }

  const search = new URLSearchParams({
    variant: String(DISPLAY_RECIPE_VARIANT),
    store_id: getPosExtId(),
  });
  const url = `${baseUrl}${RECIPE_CARD_MULTIPLE_HTML_ENDPOINT}?${search.toString()}`;

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify({ contexts }),
  }).then(async (res) => {
    if (!res.ok) {
      throw new Error(`[Mealz] ${url} fetch failed: ${res.status} ${res.statusText}`);
    }

    const payload = await res.json();
    return payload?.data ?? [];
  });
}

function buildRecipeCardContexts(listChildren) {
  const products = getProductsInList(listChildren);
  const contexts = [];

  cardsPositionsByScreenWidth().forEach((position) => {
    const precedingProduct = products[position.positionOfPrecedingProduct];
    const followingProduct = products[position.positionOfPrecedingProduct + 1];
    if (!precedingProduct || !followingProduct) {
      return;
    }

    const productIds = getProductsIds([precedingProduct, followingProduct]);
    if (productIds.length === 0 || productIds[0] === "") {
      return;
    }

    contexts.push({
      position: position.cardPosition,
      productIds,
    });
  });

  return contexts;
}

/* ******************************************************* CARD POSITIONS ******************************************************** */

/**
 * @returns An array of couples of the *final* positions of the cards and the *initial* position of their preceding product
 */
function cardsPositionsByScreenWidth() {
  const productsPosition = [3, 9, 21, 28];

  // cards should be injected after products at initial position p, which makes them be in the final position p + i + 1;
  return productsPosition.map((p, i) => ({
    positionOfPrecedingProduct: p,
    cardPosition: p + i + 1,
  }));
}

/* ******************************************************* CARD INJECTION AND REMOVAL ******************************************************** */

async function removeCardsIfNecessary() {
  if (!(await isAvailable())) {
    document.querySelectorAll(".miamRecipeCardContainer").forEach((card) => card.remove());
  }
}

/**
 * If we can find the product list, try to inject a card for each position
 */
async function injectCards() {
  if (await isAvailable()) {
    const cardMainContainer = getCardMainContainer();
    if (cardMainContainer && cardMainContainer.children) {
      productsAndCardPositions.forEach((position) => {
        injectCardIfNecessary(position);
      });
    }
  }
}

/**
 * If the element at position is not a recipe-card, try to add a card at the position or move the card that should be at position
 */
function injectCardIfNecessary(position) {
  // Skip injection if this position is marked as hidden
  if (hiddenPositions.has(position.cardPosition)) {
    return;
  }

  const list = getCardMainContainer();
  const products = getProductsAndCardsInList(list.children);
  const productAtPosition = products[position.cardPosition];
  
  // Check if there's already a card at the correct position
  if (productAtPosition && +productAtPosition.dataset.intendedPosition === position.cardPosition) {
    return; // Card is already correctly positioned
  }
  
  if (
    productAtPosition &&
    +productAtPosition.dataset.intendedPosition !== position.cardPosition
  ) {
    insertCardAfterProduct(position);
  }
}

/**
 * If the card for the position is on the page, move it at the position
 * Else, try to add a new card at position
 */
function insertCardAfterProduct(position) {
  const list = getCardMainContainer();
  const products = getProductsInList(list.children);
  const precedingProduct = products[position.positionOfPrecedingProduct];
  const followingProduct = products[position.positionOfPrecedingProduct + 1];
  if (precedingProduct && followingProduct) {
    // Try to get the card if already in the DOM
    let cardContainer = searchExistingCardForPosition(position);
    if (!cardContainer) {
      // Try to create card
      const productsIds = getProductsIds([precedingProduct, followingProduct]);
      if (productsIds.length !== 0 && productsIds[0] !== "") {
        cardContainer = createCard(productsIds, position.cardPosition);
        if (cardContainer) {
          // Add a dataset value to keep track of where the card should be and compare with where it actually is
          cardContainer.dataset.intendedPosition = position.cardPosition;
        }
      }
    }
    if (cardContainer) {
      precedingProduct.after(cardContainer);
    }
  }
}

function searchExistingCardForPosition(position) {
  return document.querySelector(`[data-intended-position="${position.cardPosition}"]`);
}

/* ******************************************************* CARD CONTAINER CREATION ******************************************************** */

/**
 * Create the card and the container around it
 */
function createCard(productsIds, position) {
  const html = ssrCardsById?.[position];
  if (!html) {
    return null;
  }

  const cardContainer = document.createElement("li");
  cardContainer.classList.add("grid-tile", "small-6", "medium-6");
  cardContainer.innerHTML = html;
  return cardContainer;
}

/* ******************************************************* UTILS ******************************************************** */

/**
 * Find the product list
 */
function getCardMainContainer() {
  return document.getElementsByClassName("tiles-container")[0];
}

export function isShelf() {
  const list = getCardMainContainer()?.children;
  return list?.length > 0 && getProductsInList(list).length > 0;
}

function getProductsInList(list) {
  return Array.from(list).filter((x) => !!x.dataset.tcProductTile);
}

function getProductsAndCardsInList(list) {
  return Array.from(list).filter((x) => !!x.dataset.tcProductTile || !!x.dataset.intendedPosition);
}

function getProductsIds(products) {
  return products.map((product) => product.querySelector("[data-product-tile]")?.dataset.itemid || "");
}















